import React, { useCallback, useEffect, useRef, useState } from 'react';
import drawUtils from '../pages/LessonPage/drawUtils';
import { DeleteForever, MoreVert } from '@material-ui/icons';
import LinkIcon from '@material-ui/icons/Link';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { makeStyles } from '@material-ui/core/styles';
import StyledMenu from '../MyComponents/Drawer/StyledMenu';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { FormattedMessage, useIntl } from 'react-intl';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import TextFormatIcon from '@material-ui/icons/TextFormat';
import { CirclePicker } from 'react-color';
import TextFormatPanel from '../MyComponents/Drawer/Tools/TextFormatPanel';
import Checkbox from '@material-ui/core/Checkbox';
import linkPreviewApi from '../Api/linkPreviewApi';

const useStyles = makeStyles(() => ({
    editCard: {
        position: 'absolute',
        borderRadius: '4px',
        border: '1px solid rgba(0,0,0,0.3)',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        fontFamily: 'inherit',
        zIndex: 0,
        '&:hover': {
            borderColor: 'rgba(87,78,51,0.8)',
        },
    },
    editCardActive: {
        boxShadow: '0 0 0 2px #07767D33',
        borderColor: '#07767D',
    },
    dragHandle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'move',
        width: '20px',
        flexShrink: 0,
        color: 'rgba(0,0,0,0.3)',
        borderRight: '1px solid rgba(0,0,0,0.1)',
        touchAction: 'none',
        '&:hover': {
            color: 'rgba(0,0,0,0.5)',
        },
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '3px 2px',
        flex: 1,
        minWidth: 0,
    },
    textArea: {
        width: '100%',
        border: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.15)',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        outline: 'none',
        padding: '1px 0',
        boxSizing: 'border-box',
        resize: 'none',
        overflow: 'hidden',
        lineHeight: 1.3,
        '&:focus': {
            borderBottomColor: '#07767D',
        },
    },
    inputField: {
        width: '100%',
        border: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.15)',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        outline: 'none',
        padding: '1px 0',
        boxSizing: 'border-box',
        '&:focus': {
            borderBottomColor: '#07767D',
        },
    },
    linkIconContainer: {
        width: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        alignSelf: 'stretch',
        borderRight: '1px solid rgba(0,0,0,0.1)',
    },
    linkIconSmall: {
        width: '14px',
        height: '14px',
        color: '#07767D',
    },
    previewImage: {
        width: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    previewToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        fontSize: '11px',
        color: '#666',
        userSelect: 'none',
    },
    buttonRemoveComponent: {
        position: 'absolute',
        cursor: 'pointer',
        right: '-18px',
        top: '-18px',
        width: '0.7em',
        height: '0.7em',
        transition: 'transform 0.3s',
        '&:hover': {
            transform: 'scale(1.8)',
        },
    },
    menuText: {
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: '1.43rem',
        letterSpacing: '-0.01rem',
        paddingTop: '4px',
        paddingBottom: '4px',
    },
    sketchPicker: { padding: '1rem' },
}));

const MAIN_MENU = 'MAIN_MENU';
const SIZE_MENU = 'SIZE_MENU';
const COLOR_MENU = 'COLOR_MENU';
const TEXT_FLUSH_INTERVAL = 2000;
const PREVIEW_FETCH_DELAY = 800;
const MIN_CARD_WIDTH = 120;
const MAX_CARD_WIDTH = 500;
const MIN_CARD_WIDTH_WITH_IMAGE = 200;
const CARD_PADDING = 46; // drag handle + horizontal padding + border + buffer

let measureCanvas = null;
function measureTextWidth(str, fontSizePx) {
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    let ctx = measureCanvas.getContext('2d');
    ctx.font = fontSizePx + 'px Arial';
    let lines = (str || '').split('\n');
    let max = 0;
    for (let i = 0; i < lines.length; i++) {
        let w = ctx.measureText(lines[i]).width;
        if (w > max) max = w;
    }
    return max;
}

function ensureProtocol(url) {
    if (!url) return url;
    let trimmed = url.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
        return 'https://' + trimmed;
    }
    return trimmed;
}

const LinkBoxForSticky = (props) => {
    const classes = useStyles();
    const intl = useIntl();

    let [toolWeight, setWeight] = useState(props.weight);
    let [color, setColor] = useState(props.color);
    let [text, setText] = useState(props.text || '');
    let [url, setUrl] = useState(props.url || '');
    let [currentFontSize, setCurrentFontSize] = useState(props.fontSize);
    let [fontFamily, setFontFamily] = useState((props.fontFamily === 'Arial' ? null : props.fontFamily) || 'Arial, Helvetica, sans-serif');
    let [fontStyle, setFontStyle] = useState(props.fontStyle || 'normal');
    let [currentFontWeight, setCurrentFontWeight] = useState(props.fontWeight || 'normal');
    let [textDecoration, setTextDecoration] = useState(props.textDecoration || 'none');
    const [previewImage, setPreviewImage] = useState(props.previewImage || null);
    const [showPreview, setShowPreview] = useState(props.showPreview !== false);
    const [menuState, setMenuState] = useState(null);
    const [anchorMenu, setAnchorMenu] = useState(null);
    const [posX, setPosX] = useState(props.x);
    const [posY, setPosY] = useState(props.y);
    let urlRef = useRef(null);
    let textRef = useRef(null);
    let dragState = useRef(null);
    let cardRef = useRef(null);
    let flushRef = useRef({
        flushedText: props.text || '',
        flushedUrl: props.url || '',
        textToFlush: props.text || '',
        urlToFlush: props.url || '',
        timer: null,
    });

    useEffect(() => {
        setPosX(props.x);
        setPosY(props.y);
    }, [props.x, props.y]);

    useEffect(() => {
        setText(props.text || '');
        setUrl(props.url || '');
        setPreviewImage(props.previewImage || null);
        setShowPreview(props.showPreview !== false);
        if (flushRef.current.timer) {
            clearTimeout(flushRef.current.timer);
        }
        flushRef.current.timer = null;
        flushRef.current.flushedText = props.text || '';
        flushRef.current.flushedUrl = props.url || '';
        flushRef.current.textToFlush = props.text || '';
        flushRef.current.urlToFlush = props.url || '';
    }, [props.text, props.url, props.previewImage, props.showPreview]);

    useEffect(() => { setWeight(props.weight); }, [props.weight]);
    useEffect(() => { setColor(props.color); }, [props.color]);

    // Fetch link preview when URL changes
    useEffect(() => {
        if (!url || url.trim().length < 5) {
            return;
        }
        let cancelled = false;
        let timer = setTimeout(async () => {
            try {
                let result = await linkPreviewApi.fetchLinkPreview(ensureProtocol(url));
                if (cancelled) return;
                if (result && result.data && result.data.ogImage) {
                    setPreviewImage(result.data.ogImage);
                    // Pass current text/url to avoid losing unflushed edits
                    let currentText = flushRef.current.textToFlush;
                    let currentUrl = flushRef.current.urlToFlush;
                    props.updateStickyLinkInfo(props.id, currentText, currentUrl, null, null, null, null, null, null, result.data.ogImage);
                    flushRef.current.flushedText = currentText;
                    flushRef.current.flushedUrl = currentUrl;
                }
            } catch (e) {
                // ignore preview fetch failures
            }
        }, PREVIEW_FETCH_DELAY);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

    const clampPosition = useCallback((x, y) => {
        let rect = drawUtils.getCurrentCanvasRect(props.email);
        let maxX = rect ? rect.width - 20 : Infinity;
        let maxY = rect ? rect.height - 20 : Infinity;
        return {
            x: Math.max(20, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, maxY)),
        };
    }, [props.email]);

    const onPointerMove = useCallback((e) => {
        if (!dragState.current) return;
        let dx = e.clientX - dragState.current.startClientX;
        let dy = e.clientY - dragState.current.startClientY;
        let clamped = clampPosition(dragState.current.startX + dx, dragState.current.startY + dy);
        setPosX(clamped.x);
        setPosY(clamped.y);
    }, [clampPosition]);

    const onPointerUp = useCallback((e) => {
        if (!dragState.current) return;
        let dx = e.clientX - dragState.current.startClientX;
        let dy = e.clientY - dragState.current.startClientY;
        let clamped = clampPosition(dragState.current.startX + dx, dragState.current.startY + dy);
        let captureEl = dragState.current.captureEl;
        let pointerId = dragState.current.pointerId;
        dragState.current = null;
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        document.removeEventListener('pointercancel', onPointerUp);
        if (captureEl && pointerId != null && captureEl.hasPointerCapture && captureEl.hasPointerCapture(pointerId)) {
            captureEl.releasePointerCapture(pointerId);
        }

        let scaledPoint = drawUtils.scalePoint(props.startPoint, clamped.x, clamped.y);
        props.updateStickyLinkInfo(props.id, text, url, scaledPoint.x, scaledPoint.y);
    }, [props.startPoint, props.id, props.updateStickyLinkInfo, text, url, onPointerMove, clampPosition]);

    const onDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragState.current = {
            startClientX: e.clientX,
            startClientY: e.clientY,
            startX: posX,
            startY: posY,
            pointerId: e.pointerId,
            captureEl: e.currentTarget,
        };
        if (e.currentTarget && e.currentTarget.setPointerCapture) {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        }
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
        document.addEventListener('pointercancel', onPointerUp);
    }, [posX, posY, onPointerMove, onPointerUp]);

    const scheduleFlush = () => {
        if (!flushRef.current.timer) {
            flushRef.current.timer = setTimeout(() => {
                if (
                    flushRef.current.flushedText !== flushRef.current.textToFlush ||
                    flushRef.current.flushedUrl !== flushRef.current.urlToFlush
                ) {
                    props.updateStickyLinkInfo(props.id, flushRef.current.textToFlush, flushRef.current.urlToFlush);
                    flushRef.current.flushedText = flushRef.current.textToFlush;
                    flushRef.current.flushedUrl = flushRef.current.urlToFlush;
                }
                flushRef.current.timer = null;
            }, TEXT_FLUSH_INTERVAL);
        }
    };

    const textChange = (e) => {
        let val = e.currentTarget.value;
        setText(val);
        flushRef.current.textToFlush = val;
        scheduleFlush();
    };

    const urlChange = (e) => {
        let val = e.currentTarget.value;
        setUrl(val);
        flushRef.current.urlToFlush = val;
        scheduleFlush();
    };

    const handleMenuOpen = (event) => {
        if (!event) { handleMenuClose(); return; }
        setAnchorMenu(event.currentTarget);
        setMenuState(MAIN_MENU);
    };
    const handleMenuClose = () => {
        setAnchorMenu(null);
        setMenuState(null);
    };

    const flushNow = () => {
        setTimeout(() => {
            if (cardRef.current && cardRef.current.contains(document.activeElement)) {
                return;
            }
            if (flushRef.current.timer) {
                clearTimeout(flushRef.current.timer);
                flushRef.current.timer = null;
            }
            let t = flushRef.current.textToFlush;
            let u = flushRef.current.urlToFlush;
            if (t.length === 0 && u.length === 0) {
                props.removeElement(props.id);
                return;
            }
            if (flushRef.current.flushedText !== t || flushRef.current.flushedUrl !== u) {
                props.updateStickyLinkInfo(props.id, t, u);
                flushRef.current.flushedText = t;
                flushRef.current.flushedUrl = u;
            }
        }, 0);
    };

    let decremental = (window.zoomState.zoomLevel * 50 + 100) / 100;
    let canvasRect = drawUtils.getCurrentCanvasRect(props.email);
    let scaleFactor = canvasRect.width / drawUtils.CANVAS_ACTUAL_WIDTH;
    let displayScale = scaleFactor * decremental;
    let baseFontSize;
    if (currentFontSize != null) {
        let canvas = drawUtils.getCanvas();
        let canvasHeight = (canvas && canvas.height) ? canvas.height : drawUtils.CANVAS_ACTUAL_HEIGHT;
        baseFontSize = canvasHeight * currentFontSize / 100;
    } else {
        baseFontSize = drawUtils.computeFontPixel(toolWeight) * 0.6;
    }
    let fontSize = displayScale * baseFontSize;
    let fontPx = fontSize + 'px';
    let linkTextStyle = {
        fontSize: fontPx,
        color,
        fontFamily,
        fontStyle,
        fontWeight: currentFontWeight,
        textDecoration,
    };
    let scaledPadding = CARD_PADDING * displayScale;
    let scaledMinWidth = MIN_CARD_WIDTH * displayScale;
    let scaledMaxWidth = MAX_CARD_WIDTH * displayScale;
    let scaledMinWidthWithImage = MIN_CARD_WIDTH_WITH_IMAGE * displayScale;

    const autoGrowTextArea = () => {
        if (textRef.current) {
            textRef.current.style.height = 'auto';
            textRef.current.style.height = textRef.current.scrollHeight + 'px';
        }
    };

    useEffect(() => {
        requestAnimationFrame(autoGrowTextArea);
    }, [text, fontPx, props.isSelectable]);

    // Auto-focus the URL input when entering edit mode
    useEffect(() => {
        if (props.isSelectable && urlRef.current) {
            urlRef.current.focus({ preventScroll: true });
        }
    }, [props.isSelectable]);

    if (props.isSelectable) {
        let menu = (
            <List>
                <ListItem button onClick={() => { props.setDialogClear(props.id); }} className={classes.menuText}>
                    <ListItemIcon><DeleteForever color='secondary' /></ListItemIcon>
                    <FormattedMessage id='tooltip.lessonPage.remove' />
                </ListItem>
                <ListItem button onClick={() => { setMenuState(SIZE_MENU); }} className={classes.menuText}>
                    <ListItemIcon><TextFormatIcon style={{ verticalAlign: 'middle' }} color={'primary'} /></ListItemIcon>
                    <FormattedMessage id='tooltip.lessonPage.font' />
                </ListItem>
                <ListItem button onClick={() => { setMenuState(COLOR_MENU); }} className={classes.menuText}>
                    <ListItemIcon>
                        <span style={{ borderRadius: '50%', display: 'inline-block', border: '1px solid white', background: color, width: '24px', height: '24px' }} />
                    </ListItemIcon>
                    <FormattedMessage id='tooltip.lessonPage.color' />
                </ListItem>
            </List>
        );
        if (menuState === SIZE_MENU) {
            const updateLinkOpts = (opts) => {
                props.updateStickyLinkInfo(props.id, null, null, null, null, null, null, null, null, null, null, opts);
            };
            menu = (
                <TextFormatPanel
                    fontSize={currentFontSize}
                    fontWeight={toolWeight}
                    fontFamily={fontFamily}
                    fontStyle={fontStyle}
                    bold={currentFontWeight}
                    textDecoration={textDecoration}
                    showColor={false}
                    onFontSizeChange={null}
                    onFontSizeCommit={(val) => { setCurrentFontSize(val); updateLinkOpts({ fontSize: val }); }}
                    onFontFamilyChange={(val) => { setFontFamily(val); updateLinkOpts({ fontFamily: val }); }}
                    onFontStyleChange={(val) => { setFontStyle(val); updateLinkOpts({ fontStyle: val }); }}
                    onBoldChange={(val) => { setCurrentFontWeight(val); updateLinkOpts({ fontWeight: val }); }}
                    onTextDecorationChange={(val) => { setTextDecoration(val); updateLinkOpts({ textDecoration: val }); }}
                />
            );
        }
        if (menuState === COLOR_MENU) {
            menu = (
                <div className={classes.sketchPicker}>
                    <CirclePicker
                        disableAlpha
                        color={color}
                        colors={[
                            '#000000', '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
                            '#2196f3', '#03a9f4', '#009688', '#4caf50', '#8bc34a', '#cddc39',
                            '#ffeb3b', '#ffc107', '#ff9800', '#ff5722', '#795548', '#607d8b',
                        ]}
                        onChangeComplete={(c) => {
                            props.updateStickyLinkInfo(props.id, null, null, null, null, null, null, c.hex, null);
                            handleMenuClose();
                        }}
                    />
                </div>
            );
        }

        let textWidth = measureTextWidth(text, fontSize) + scaledPadding;
        let urlWidth = measureTextWidth(url, fontSize) + scaledPadding;
        let displayText = text || url || '';
        let displayWidth = measureTextWidth(displayText, fontSize) + scaledPadding;
        let contentWidth = Math.max(textWidth, urlWidth, displayWidth);
        let editMinWidth = previewImage ? scaledMinWidthWithImage : scaledMinWidth;
        let cardWidth = Math.min(scaledMaxWidth, Math.max(editMinWidth, contentWidth));

        return (
            <div
                ref={cardRef}
                className={`${classes.editCard} ${props.localSelectedItem === props.id ? classes.editCardActive : ''}`}
                style={{
                    position: 'absolute',
                    left: posX + 'px',
                    top: posY + 'px',
                    width: cardWidth + 'px',
                    color,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    props.setLocalSelectedItem(props.id);
                }}
                onTouchStart={(e) => { e.stopPropagation(); }}
            >
                {/* Drag handle — only this area initiates drag */}
                <div
                    className={classes.dragHandle}
                    onPointerDown={onDragStart}
                >
                    <DragIndicatorIcon style={{ fontSize: '12px' }} />
                </div>

                {/* Fields area — inputs receive focus normally */}
                <div className={classes.fieldsContainer}>
                    {props.localSelectedItem === props.id && (
                        <div style={{ position: 'absolute', top: 0, right: 0 }}>
                            <MoreVert
                                color='primary'
                                onClick={(e) => {
                                    handleMenuOpen(e);
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onTouchStart={(e) => {
                                    handleMenuOpen(e);
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                className={classes.buttonRemoveComponent}
                            />
                            <StyledMenu
                                id={`link-box-edit-${props.id}`}
                                anchorEl={anchorMenu}
                                keepMounted
                                open={Boolean(anchorMenu)}
                                onClose={handleMenuClose}
                            >
                                {menu}
                            </StyledMenu>
                        </div>
                    )}
                    <input
                        ref={urlRef}
                        value={url}
                        onChange={urlChange}
                        onBlur={flushNow}
                        className={classes.inputField}
                        style={{ fontSize: Math.round(fontSize * 0.85) + 'px', color: '#666' }}
                        placeholder='https://...'
                        id={`link-url-sticky-${props.id}`}
                    />
                    <textarea
                        ref={textRef}
                        value={text}
                        onChange={textChange}
                        onBlur={flushNow}
                        rows={1}
                        className={classes.textArea}
                        style={linkTextStyle}
                        placeholder={intl.formatMessage({ id: 'tooltip.lessonPage.label' })}
                        id={`link-text-sticky-${props.id}`}
                    />
                    {previewImage && (
                        <div className={classes.previewToggle}>
                            <Checkbox
                                checked={showPreview}
                                onChange={(e) => {
                                    let val = e.target.checked;
                                    setShowPreview(val);
                                    props.updateStickyLinkInfo(props.id, null, null, null, null, null, null, null, null, null, val);
                                }}
                                size='small'
                                color='primary'
                                style={{ padding: 0 }}
                            />
                            <FormattedMessage id='tooltip.lessonPage.showPreview' defaultMessage='Show preview' />
                        </div>
                    )}
                    {previewImage && showPreview && (
                        <img
                            src={previewImage}
                            className={classes.previewImage}
                            onError={(e) => { e.target.style.display = 'none'; }}
                            alt=''
                        />
                    )}
                </div>
            </div>
        );
    } else {
        let displayText = text || url || '';
        let hasUrl = url && url.trim().length > 0;
        let displayTextWidth = measureTextWidth(displayText, fontSize) + scaledPadding;
        let displayUrlWidth = measureTextWidth(url, fontSize) + scaledPadding;
        let displayContentWidth = Math.max(displayTextWidth, displayUrlWidth);
        let minWidth = previewImage ? scaledMinWidthWithImage : scaledMinWidth;
        let viewWidth = Math.min(scaledMaxWidth, Math.max(minWidth, displayContentWidth));
        let href = hasUrl ? ensureProtocol(url) : undefined;

        let cardStyle = {
            position: 'absolute',
            left: props.x + 'px',
            top: props.y + 'px',
            width: viewWidth + 'px',
            color,
            pointerEvents: hasUrl ? 'auto' : 'none',
            cursor: hasUrl ? 'pointer' : 'default',
        };

        let inner = (
            <>
                <div className={classes.linkIconContainer}>
                    <LinkIcon className={classes.linkIconSmall} />
                </div>
                <div className={classes.fieldsContainer}>
                    <span style={{ ...linkTextStyle, wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.3 }}>
                        {displayText}
                    </span>
                    {previewImage && showPreview && (
                        <img
                            src={previewImage}
                            className={classes.previewImage}
                            onError={(e) => { e.target.style.display = 'none'; }}
                            alt=''
                        />
                    )}
                </div>
            </>
        );

        if (hasUrl) {
            return (
                <a
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={url}
                    className={classes.editCard}
                    style={{ ...cardStyle, textDecoration: 'none', zIndex: 1 }}
                >
                    {inner}
                </a>
            );
        }

        return (
            <div
                className={classes.editCard}
                style={cardStyle}
            >
                {inner}
            </div>
        );
    }
};

export default LinkBoxForSticky;
